import { serviceJsonLd, SHARED_STEPS, SHARED_STATS } from "../types";
import type { LandingContent } from "../types";

const faqs = [
  {
    q: "Which TMS, WMS or ERP systems do you integrate with?",
    a: "We integrate with the systems you already run — via API, EDI or RPA where no API exists. Common stacks include project44 and MacroPoint for visibility, major TMS/WMS platforms, QuickBooks and common accounting tools, plus email, spreadsheets and customer portals. If your operation runs on it, we can connect to it.",
  },
  {
    q: "How much manual work can really be automated?",
    a: "Industry benchmarks put AI freight documentation and back-office automation at up to 85% reduction in manual processing, with payback typically in 3–6 months. Your free assessment will size the opportunity for your specific operation before you commit to anything.",
  },
  {
    q: "Will this replace my back-office staff?",
    a: "It lets them do higher-value work — or lets you grow shipment volume without growing payroll at the same pace. Most clients redeploy staff to customer relationships, exception handling and growth instead of data entry.",
  },
  {
    q: "How long does a logistics automation deployment take?",
    a: "From audit to automation in about 8 weeks for a first scoped workflow (e.g., POD processing or track-and-trace updates). Multi-workflow rollouts are sequenced by ROI so the fastest payback goes live first.",
  },
  {
    q: "Is our operational data secure?",
    a: "Yes. Deployments run with encryption in transit and at rest, role-based access controls and full audit logs. We sign NDAs before discovery and design data flows around your security requirements.",
  },
  {
    q: "What does it cost?",
    a: "First scoped projects typically land between $10,000 and $30,000 depending on document volume, integrations and workflows. The free assessment gives you projected savings and payback period up front — in writing.",
  },
];

export const logisticsContent: LandingContent = {
  seo: {
    title: "AI Automation for Logistics Companies — AIdeal Media",
    description:
      "AI automation for logistics companies: freight documentation, POD processing, carrier updates and 24/7 customer service on autopilot. Cut manual back-office work by up to 85%.",
    keywords: "ai automation for logistics, logistics automation company, ai for 3pl, freight document automation",
    canonical: "https://www.aidealmedia.info/industries/ai-automation-for-logistics/",
    ogImage: "https://www.aidealmedia.info/img/hero-bg.jpg",
    jsonLd: serviceJsonLd({
      url: "https://www.aidealmedia.info/industries/ai-automation-for-logistics/",
      name: "AI Automation for Logistics Companies",
      description:
        "AI automation for logistics companies: freight documentation, POD processing, carrier communication and customer service automation.",
      faqs,
    }),
  },
  badge: "3PLs · Freight brokers · Warehousing · Distributors",
  h1a: "AI Automation for ",
  h1gradient: "Logistics Companies",
  h1b: "",
  sub: "Margins in logistics are won in the back office. We automate the repetitive document, communication and tracking work that eats your team's day — so you scale volume without scaling payroll.",
  problemTitle: "Your trucks move freight. Your team moves paper.",
  problemIntro:
    "In freight and distribution, the margin difference between two companies moving similar volume is usually back-office labor. Here's where it bleeds:",
  pains: [
    {
      icon: "ri-file-copy-2-line",
      title: "Manual data entry everywhere",
      text: "BOLs, PODs, invoices and rate confirmations retyped across TMS, email and spreadsheets — slow, error-prone and expensive.",
    },
    {
      icon: "ri-truck-line",
      title: "Chasing PODs and status updates",
      text: "Your team spends its day asking carriers 'where's the POD?' and customers asking you 'where's my shipment?' — instead of moving freight.",
    },
    {
      icon: "ri-customer-service-line",
      title: "Customer service drowning in WISMO",
      text: "'Where is my order?' tickets pile up while your reps copy-paste tracking links. Automatable to near zero.",
    },
  ],
  servicesTitle: "Automation for the workflows that eat margin",
  servicesIntro:
    "We enter your operation, map how each department actually works, automate the repetitive workflows and connect AI to the systems you already run.",
  services: [
    {
      icon: "ri-file-text-line",
      title: "Freight Document Automation",
      text: "BOLs, PODs, invoices and rate confirmations extracted, validated and filed automatically — up to 85% less manual processing.",
    },
    {
      icon: "ri-radar-line",
      title: "Track & Trace Updates",
      text: "Proactive shipment status updates to customers before they ask — fewer WISMO tickets, happier accounts.",
    },
    {
      icon: "ri-links-line",
      title: "Carrier Communication",
      text: "Automated check calls, POD requests and appointment scheduling with carriers across email, SMS and voice.",
    },
    {
      icon: "ri-bill-line",
      title: "Invoice Audit & Reconciliation",
      text: "Carrier invoices matched against quotes and contracts automatically — overcharges flagged before you pay them.",
    },
    {
      icon: "ri-headphone-line",
      title: "24/7 AI Customer Service",
      text: "Instant answers on tracking, ETAs and documentation across chat, email and voice — with human escalation.",
    },
    {
      icon: "ri-dashboard-3-line",
      title: "Operational Dashboards",
      text: "One view of volume, margin per lane, on-time performance and document exceptions — with AI alerts.",
    },
  ],
  bandTitle: "The economics are the point",
  bandText:
    "Freight pricing is compressed, so growth has to come from efficiency. Our engagements target the math that matters:",
  bandPoints: [
    "Up to 85% reduction in manual document processing (industry benchmark)",
    "Typical payback of 3–6 months on document automation workflows",
    "Grow shipment volume while payroll grows at a far lower rate",
    "Fastest-payback workflow goes live first — ROI funds the rest",
  ],
  processTitle: "From audit to automation in 8 weeks",
  processIntro: "A transparent, results-driven process built on measurable KPIs and performance-backed contracts.",
  steps: SHARED_STEPS,
  stats: SHARED_STATS,
  faqTitle: "Frequently asked questions",
  faqs,
  relatedTitle: "Explore more",
  related: [
    {
      href: "/ai-readiness-assessment/",
      label: "Free AI Readiness Assessment",
      desc: "Size your automation opportunity — in writing.",
    },
    {
      href: "/industries/ai-agents-for-law-firms/",
      label: "AI Agents for Law Firms",
      desc: "24/7 intake, triage and automation for firms.",
    },
    {
      href: "/ai-automation-agency-houston/",
      label: "AI Automation Agency in Houston",
      desc: "Local partner for Texas businesses.",
    },
  ],
  ctaTitle: "Find your fastest payback",
  ctaText:
    "Book a free assessment. We'll map your back-office workflows and show you — in writing, within 24 hours — which automation pays for itself first.",
};
